import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaExternalLinkAlt } from "react-icons/fa";
import api from "../../utils/api/axiosInstance";
import getImageUrl, { resolveProductImagePath } from "../../utils/imageUtils";
import { openTrackedOutboundUrl } from "../../utils/trackOutboundClick";
import {
  formatCheckedAgo,
  visibleRetailerOffers,
} from "../product/ProductRetailerOffers";
import { buildPickReason, presentTradeoff } from "../../utils/decisionPickCopy";
import { toPublicDecisionReason } from "../../utils/publicDecisionCopy";
import styles from "../../styles/components/home/HelpMeDecideSection.module.css";

function formatCatalogPrice(price) {
  const amount = Number(price);
  if (!Number.isFinite(amount)) return null;
  return `$${amount.toLocaleString()}`;
}

function formatOfferAmount(amount, currency) {
  const formatted = Number(amount).toLocaleString("en-US");
  return currency === "USD" || !currency ? `$${formatted}` : `${currency} ${formatted}`;
}

const DecisionReceipt = ({
  product,
  reason = "",
  tradeoff = "",
  sessionId = null,
  offers,
  loadOffers = false,
  publicView = false,
  searchParams = null,
  otherNames = [],
  role = null,
  onChangePick,
  onClear,
  onShare,
  onWatch,
  onStopWatch,
  onAnswer,
  priceWatch = null,
  askFollowUp = false,
  followUpAnswer = "",
  reaskCount = 0,
  alternatives = [],
  cohortSentence = "",
  saving = false,
  shareNotice = "",
  error = "",
}) => {
  const [loadedOffers, setLoadedOffers] = useState(null);
  const [otherProductId, setOtherProductId] = useState("");
  const [choosingOther, setChoosingOther] = useState(false);
  const [confirmedAnswer, setConfirmedAnswer] = useState("");
  const productId = product?._id || product?.id;

  useEffect(() => {
    if (!loadOffers || !productId || offers) return undefined;
    let cancelled = false;
    api
      .get(`/products/${productId}`)
      .then((res) => {
        if (!cancelled) setLoadedOffers(visibleRetailerOffers(res.data));
      })
      .catch(() => {
        if (!cancelled) setLoadedOffers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [loadOffers, productId, offers]);

  if (!product) return null;

  const catalogPrice = formatCatalogPrice(product.price);
  const visibleOffers = offers
    ? visibleRetailerOffers({ liveOffers: offers })
    : loadedOffers || [];
  const focusedReason = buildPickReason({
    product,
    advice: reason,
    searchParams,
    otherNames,
    role,
  });
  const displayReason = publicView
    ? toPublicDecisionReason(focusedReason)
    : focusedReason;
  const displayTradeoff = presentTradeoff(tradeoff);
  const productPath = productId ? `/product/${productId}` : null;

  const confirmationCopy = {
    bought_it: "Saved. You bought this one.",
    bought_other: "Saved. You bought a different one.",
    waiting_on_price: "Saved. We'll ask again about the price.",
  };
  const confirmationAnswer =
    confirmedAnswer || (!askFollowUp ? followUpAnswer : "");
  const confirmation = confirmationCopy[confirmationAnswer] || "";

  const submitAnswer = async (payload) => {
    const result = await onAnswer?.(payload);
    if (result) setConfirmedAnswer(payload.answer);
  };

  const openOffer = (offer) => {
    if (!offer?.url) return;
    openTrackedOutboundUrl({
      url: offer.url,
      productId,
      source: publicView ? "public_decision" : "decision_receipt",
      linkType: "retailer",
      sessionId: publicView ? undefined : sessionId || undefined,
      metadata: { retailer: offer.retailerName },
    });
  };

  return (
    <article
      className={`${styles.receipt}${publicView ? ` ${styles.receiptPublic}` : ""}`}
      aria-labelledby="decision-receipt-title"
    >
      <div className={styles.receiptMain}>
        <div className={styles.receiptImageWrap}>
          <img
            src={getImageUrl(resolveProductImagePath(product))}
            alt=""
            className={styles.receiptImage}
          />
        </div>
        <div className={styles.receiptCopy}>
          {publicView && (
            <p className={styles.receiptKicker}>A Simpary decision</p>
          )}
          {productPath && !publicView ? (
            <Link to={productPath} className={styles.receiptNameLink}>
              <h3 id="decision-receipt-title" className={styles.receiptName}>
                {product.name}
              </h3>
            </Link>
          ) : (
            <h3 id="decision-receipt-title" className={styles.receiptName}>
              {product.name}
            </h3>
          )}
          {catalogPrice && <p className={styles.receiptPrice}>{catalogPrice}</p>}
          {publicView ? (
            <p className={styles.receiptReason}>
              <span className={styles.receiptReasonLabel}>
                They picked this because…
              </span>
              <span className={styles.receiptReasonText}>{displayReason}</span>
            </p>
          ) : (
            displayReason && (
              <p className={styles.receiptReason}>
                <span className={styles.receiptReasonLabel}>Why it fits:</span>{" "}
                {displayReason}
              </p>
            )
          )}
          {displayTradeoff && (
            <p className={styles.receiptTradeoff}>{displayTradeoff}</p>
          )}
          {cohortSentence ? (
            <p className={styles.cohortNote}>{cohortSentence}</p>
          ) : null}
        </div>
      </div>

      {visibleOffers.length > 0 && (
        <section className={styles.whereToBuy} aria-label="Where to buy">
          <h4 className={styles.whereToBuyTitle}>Where to buy</h4>
          <ul className={styles.offerList}>
            {visibleOffers.map((offer) => (
              <li
                key={`${offer.retailerName}-${offer.url}`}
                className={styles.offerRow}
              >
                <div>
                  <p className={styles.offerName}>{offer.retailerName}</p>
                  <p className={styles.offerPrice}>
                    {formatOfferAmount(offer.price ?? offer.amount, offer.currency)}
                    <span className={styles.offerChecked}>
                      {" "}
                      · {formatCheckedAgo(offer.checkedAt || offer.lastSuccessAt)}
                    </span>
                  </p>
                </div>
                <button
                  type="button"
                  className={styles.offerBtn}
                  onClick={() => openOffer(offer)}
                >
                  See at {offer.retailerName}
                  <FaExternalLinkAlt aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!publicView && (askFollowUp || confirmation) && (
        <div className={styles.followUp}>
          {confirmation ? (
            <p className={styles.followUpConfirm} role="status">
              {confirmation}
            </p>
          ) : (
            <>
          <p className={styles.followUpTitle}>Did you buy it?</p>
          <div className={styles.followUpChoices}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => submitAnswer({ answer: "bought_it" })}
              disabled={saving}
            >
              I bought it
            </button>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => setChoosingOther(true)}
              disabled={saving}
            >
              I bought a different one
            </button>
            {reaskCount < 2 && (
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => submitAnswer({ answer: "waiting_on_price" })}
                disabled={saving}
              >
                I&apos;m waiting on the price
              </button>
            )}
          </div>
            </>
          )}
          {choosingOther && !confirmation && (
            <div className={styles.followUpOther}>
              <label className={styles.followUpLabel} htmlFor="decision-other-product">
                Which one, if you want to say
              </label>
              <select
                id="decision-other-product"
                className={styles.followUpSelect}
                value={otherProductId}
                onChange={(event) => setOtherProductId(event.target.value)}
              >
                <option value="">I&apos;d rather not name it</option>
                {alternatives.map((item) => (
                  <option key={item._id || item.id} value={item._id || item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className={styles.primaryBtn}
                disabled={saving}
                onClick={() =>
                  submitAnswer({
                    answer: "bought_other",
                    otherProductId: otherProductId || undefined,
                  })
                }
              >
                Save that answer
              </button>
            </div>
          )}
        </div>
      )}

      {!publicView && (
        <div className={styles.receiptActions}>
          {priceWatch?.active ? (
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={onStopWatch}
              disabled={saving}
            >
              Stop watching
              {priceWatch.baselinePrice != null
                ? ` · below $${Number(priceWatch.baselinePrice).toLocaleString()}`
                : ""}
            </button>
          ) : (
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={onWatch}
              disabled={saving}
            >
              Watch the price
            </button>
          )}
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={onShare}
            disabled={saving}
          >
            Share
          </button>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={onChangePick}
            disabled={saving}
          >
            Change my pick
          </button>
          <button
            type="button"
            className={styles.textBtn}
            onClick={onClear}
            disabled={saving}
          >
            Clear decision
          </button>
          {shareNotice && <p className={styles.shareNotice}>{shareNotice}</p>}
        </div>
      )}
      {error && (
        <p className={styles.choiceError} role="alert">
          {error}
        </p>
      )}
    </article>
  );
};

export default DecisionReceipt;
